"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Calendar = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const moment_1 = __importDefault(require("moment"));
require("moment/locale/en-gb");
require("moment/locale/nl");
const styled_components_1 = __importDefault(require("styled-components"));
const icons_1 = require("../../atoms/icons");
const hooks_1 = require("../../shared/hooks");
const react_i18next_1 = require("react-i18next");
const utils_1 = require("./utils");
const isDateVisible = (current, visibleDates) => {
    return visibleDates.some((date) => date.isSame(current));
};
const Calendar = (0, react_1.memo)(({ initialDate = (0, moment_1.default)({ hour: 0 }), onDayChange, onWeekChange, variant, inset = false, weekView = true, onDateRangeChange, visibleDateRange, isRegularMode, disableDateClick = false }) => {
    //On designs short names was different for NL then standard js date format
    moment_1.default.updateLocale("nl", {
        weekdaysShort: ["Zo", "Ma", "Di", "Woe", "Do", "Vr", "Za"]
    });
    const [_, i18n] = (0, react_i18next_1.useTranslation)();
    const today = (0, moment_1.default)({ hour: 0 }).locale(i18n.language);
    const { isMobile } = (0, hooks_1.useRWD)();
    const [date, setDate] = (0, react_1.useState)((0, moment_1.default)(initialDate).clone().locale(i18n.language));
    const [visibleDates, setVisibleDates] = (0, react_1.useState)([]);
    const isCompact = (!variant && isMobile) || variant === "compact";
    (0, react_1.useEffect)(() => {
        if (!visibleDateRange) {
            const currentDate = (0, moment_1.default)(initialDate).clone().locale(i18n.language);
            setDate(currentDate);
            setVisibleDates((0, utils_1.prepareVisibleDate)(currentDate));
        }
    }, [visibleDateRange]);
    (0, react_1.useEffect)(() => {
        setDate(date.clone().locale(i18n.language));
        !weekView && setVisibleDates((0, utils_1.prepareVisibleDate)(date));
    }, [i18n.language]);
    (0, react_1.useEffect)(() => {
        moment_1.default.locale(i18n.language);
        const dates = isCompact ? [date] : (0, utils_1.prepareVisibleDate)(date);
        onDateRangeChange((0, utils_1.getCurrentWeekDateRange)(visibleDates));
        if (!isDateVisible(date, visibleDates) &&
            visibleDates.length !== 0 &&
            !weekView) {
            // less about 1d
            if (date
                .clone()
                .add(+1, "d")
                .isSame(visibleDates[0])) {
                setVisibleDates((0, utils_1.prepareVisibleDate)(date.clone().add(+6, "d")));
            }
            else {
                setVisibleDates((0, utils_1.prepareVisibleDate)(date));
            }
            return setDate(date);
        }
        weekView && visibleDates.length === 0 && setVisibleDates(dates);
    }, [date, isMobile, variant, i18n.language, weekView]);
    (0, react_1.useEffect)(() => {
        if (!(0, moment_1.default)(initialDate).isSame(date, "d")) {
            setDate((0, moment_1.default)(initialDate).clone().locale(i18n.language));
        }
    }, [initialDate]);
    const moveDay = (movement) => {
        if (!weekView || isRegularMode) {
            const d = date.clone().add(movement, "d");
            onDayChange && onDayChange(d);
            return setDate(d);
        }
        setDate(date.clone().add(movement, "d"));
        movement !== 0 && onDayChange && onDayChange(date.clone());
    };
    const moveWeek = (movement) => {
        const currentDate = date.clone().add(movement * 7, "d");
        setDate(currentDate);
        movement !== 0 && onWeekChange && onWeekChange(currentDate);
        setVisibleDates((0, utils_1.calculateNextVisibleWeekDates)(visibleDates, movement < 1));
    };
    return ((0, jsx_runtime_1.jsxs)(CalendarStyled, { "data-compact": isCompact, "data-inset": inset, disableDateClick: disableDateClick, children: [!isCompact && ((0, jsx_runtime_1.jsx)(icons_1.Icon, { shape: "rectangleBig", icon: ["fal", "chevron-double-left"], onClick: () => {
                    if (!weekView) {
                        moveDay(-7);
                        return setVisibleDates((0, utils_1.calculateNextVisibleWeekDates)(visibleDates, true));
                    }
                    moveWeek(-1);
                } }, void 0)), (0, jsx_runtime_1.jsx)(icons_1.Icon, { shape: "rectangleBig", icon: ["fal", "chevron-left"], onClick: () => {
                    if (isRegularMode) {
                        if (weekView && isMobile) {
                            return moveWeek(-1);
                        }
                        moveDay(-1);
                        return setVisibleDates((0, utils_1.shitVisibleDateByOne)(visibleDates));
                    }
                    if (weekView && isCompact)
                        moveWeek(-1);
                    else
                        moveDay(-1);
                } }, void 0), (0, jsx_runtime_1.jsx)("ul", { children: isCompact ? ((0, jsx_runtime_1.jsx)("li", { onClick: () => {
                        setDate(date);
                        onDayChange && onDayChange(date.clone());
                    }, children: (0, utils_1.dateToString)(date, isCompact, weekView, visibleDates) }, date.toString())) : (visibleDates.map((d) => ((0, jsx_runtime_1.jsx)("li", { "data-selected": !isRegularMode && !weekView && d.isSame(date), "data-today": d.isSame(today, "day"), onClick: () => {
                        if (disableDateClick)
                            return;
                        setDate(d);
                        onDayChange && onDayChange(d.clone());
                    }, children: (0, utils_1.dateToString)(d, isCompact, weekView, visibleDates) }, d.toString())))) }, void 0), (0, jsx_runtime_1.jsx)(icons_1.Icon, { shape: "rectangleBig", icon: ["fal", "chevron-right"], onClick: () => {
                    if (isRegularMode) {
                        if (weekView && isMobile) {
                            return moveWeek(1);
                        }
                        moveDay(1);
                        return setVisibleDates((0, utils_1.shitVisibleDateByOne)(visibleDates, false));
                    }
                    if (weekView && isCompact)
                        moveWeek(1);
                    else
                        moveDay(1);
                } }, void 0), !isCompact && ((0, jsx_runtime_1.jsx)(icons_1.Icon, { shape: "rectangleBig", icon: ["fal", "chevron-double-right"], onClick: () => {
                    if (!weekView) {
                        moveDay(7);
                        setVisibleDates((0, utils_1.calculateNextVisibleWeekDates)(visibleDates, false));
                        return;
                    }
                    moveWeek(1);
                } }, void 0))] }, void 0));
});
exports.Calendar = Calendar;
const CalendarStyled = styled_components_1.default.div `
  display: grid;
  width: 100%;
  grid-template-columns:
    minmax(1rem, 30px) minmax(1rem, 30px) minmax(380px, auto) minmax(1rem, 30px)
    minmax(1rem, 30px);
  align-items: end;
  grid-gap: 0.5rem;

  &[data-inset="true"] {
    background-color: ${({ theme }) => theme.colors.whiteBase};
    padding: ${({ theme }) => theme.spacings.m};
  }

  &[data-compact="true"] {
    grid-template-columns: 30px auto 30px;
    align-items: center;
    ul {
      grid-template-columns: auto;
      li {
        font-weight: 400 !important;
        color: ${({ theme }) => theme.colors.greyBase} !important;
        line-height: 1.25;
        padding: 0.25rem 0;
        top: 0;
        cursor: unset !important;
      }
    }
  }

  * {
    user-select: none;
  }
  div {
    font-size: 0.6569rem;
    cursor: pointer;
    height: auto;
    width: auto;
    background: transparent;
    &:first-child,
    &:nth-child(2) {
      justify-content: start;
    }

    &:last-child,
    &:nth-last-child(2) {
      justify-content: flex-end;
    }
  }

  ul {
    display: grid;
    align-self: flex-end;
    width: 100%;
    grid-template-columns: repeat(7, minmax(30px, 1fr));
    grid-gap: 1rem;
    li {
      white-space: pre;
      text-align: center;
      cursor: ${({ disableDateClick }) => disableDateClick ? "unset" : "pointer"};
      font-weight: normal;
      line-height: 1.5;
      position: relative;
      top: 0.41rem;

      &:first-child:nth-last-child(1) {
        width: 100%;
      }

      &[data-today="true"] {
        font-weight: bold;
      }
      &[data-selected="true"] {
        font-weight: bold;
        color: ${({ theme }) => theme.colors.greenBase};
      }
    }
  }
`;
//# sourceMappingURL=index.js.map